import {testIdProps} from '@github-ui/test-id-props'
import {AlertIcon, CheckCircleIcon, InfoIcon, StopIcon, XIcon} from '@primer/octicons-react'
import {Octicon} from '@primer/react/deprecated'
import {clsx} from 'clsx'
import {type ComponentProps, useCallback, useEffect, useRef} from 'react'

import styles from './toast.module.css'
import {TOAST_ANIMATION_LENGTH, type ToastItem, ToastType} from './types'

type IToastCloseButtonProps = Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>

type StyledButtonProps = ComponentProps<'button'> & {ref?: React.Ref<HTMLButtonElement>}

const StyledButton = ({ref, className, ...props}: StyledButtonProps) => (
  <button {...props} className={clsx(styles.styledButton, className)} ref={ref} />
)
StyledButton.displayName = 'StyledButton'

const ToastCloseButton = (props: IToastCloseButtonProps) => (
  <StyledButton aria-label="Close" {...props}>
    <Octicon icon={XIcon} />
  </StyledButton>
)

const DefaultIcon = <Octicon icon={InfoIcon} className={styles.Octicon} />
const SuccessIcon = <Octicon icon={CheckCircleIcon} className={styles.Octicon} />
export const WarningIcon = <Octicon icon={AlertIcon} className={styles.Octicon} />
const ErrorIcon = <Octicon icon={StopIcon} className={styles.Octicon} />

const stateMap = {
  [ToastType.default]: DefaultIcon,
  [ToastType.success]: SuccessIcon,
  [ToastType.warning]: WarningIcon,
  [ToastType.error]: ErrorIcon,
}

type IconContainerProps = ComponentProps<'div'> & {ref?: React.Ref<HTMLDivElement>}

export const IconContainer = ({ref, className, ...props}: IconContainerProps) => (
  <div {...props} className={clsx(styles.iconContainer, className)} ref={ref} />
)
IconContainer.displayName = 'IconContainer'

type StyledToastProps = ComponentProps<'div'> & {ref?: React.Ref<HTMLDivElement>}

export const StyledToast = ({ref, className, style, ...props}: StyledToastProps) => (
  <div
    ref={ref}
    {...props}
    style={{'--toast-animation-length': `${TOAST_ANIMATION_LENGTH}ms`, ...style}}
    className={clsx(styles.styledToast, className)}
  />
)
StyledToast.displayName = 'StyledToast'

type ToastActionProps = ComponentProps<'button'> & {ref?: React.Ref<HTMLButtonElement>}

export const ToastAction = ({ref, className, ...props}: ToastActionProps) => (
  <button {...props} className={clsx(styles.toastAction, className)} ref={ref} />
)
ToastAction.displayName = 'ToastAction'

interface ToastProps {
  startRemovingToast?: (id: number) => void
  toast: ToastItem
}

/**
 * Component for rendering a toast within the application
 */
const Toast = (props: ToastProps) => {
  const {toast, startRemovingToast} = props
  const callToActionRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic
      if (callToActionRef.current && event.ctrlKey && event.key === 't') {
        callToActionRef.current.focus()
        toast.timeout?.cancel()
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [toast])

  const toastIcon = toast.icon ? <Octicon icon={toast.icon} className={styles.Octicon_1} /> : stateMap[toast.type]

  const handleActionClick = useCallback(() => {
    toast.action?.handleClick()
    if (startRemovingToast) {
      startRemovingToast(toast.id)
    }
  }, [toast, startRemovingToast])

  return (
    <StyledToast role="status" className={toast.className} {...testIdProps('toast')}>
      <IconContainer className={clsx(styles.IconContainer, styles[toast.type])}>{toastIcon}</IconContainer>
      <div className={styles.Box}>
        <span className={styles.Text}>{toast.message}</span>
        {toast.action && (
          <ToastAction
            ref={callToActionRef}
            onClick={handleActionClick}
            {...testIdProps('toast-action')}
            className={styles.ToastAction}
          >
            {toast.action.text}
          </ToastAction>
        )}
      </div>
      {startRemovingToast && <ToastCloseButton onClick={() => startRemovingToast(toast.id)} />}
    </StyledToast>
  )
}

export default Toast
